import { useState } from 'react';
import { MdRestartAlt } from 'react-icons/md';
import { Alert, Divider } from 'rsuite';
import { canHaveTestClientReset, LastTestClientReset, requestTestClientReset } from '../../../services/TestClientResetService';
import { isResetRunning, useTestClientResetStore } from '../../../store/useTestClientResetStore';
import { ActionFeedbackDialog } from '../../ActionFeedbackDialog/ActionFeedbackDialog';
import styles from './NavBarUserMenuContent.module.scss';

const CONFIRMATION_DESCRIPTION =
  'Tudo o que foi feito neste cliente de teste (preços aplicados, edições, regras) volta ao estado inicial da massa ' +
  'da task. Leva alguns minutos; avisamos quando terminar.';

const describeLastReset = (lastReset?: LastTestClientReset): string | null => {
  if (!lastReset) return null;
  if (isResetRunning(lastReset)) return 'Reset em andamento…';
  const when = new Date(lastReset.created_at).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
  return lastReset.conclusion === 'success' ? `Último reset: ${when}` : `Último reset falhou (${when})`;
};

// Só no HML e só para o usuário de teste da task: volta o cliente TASK_* ao estado da massa. O estado vem do
// TestClientResetMonitor (montado com o NavBar), que segue consultando enquanto o reset roda.
export const TestClientResetMenuItem = ({ login }: { login?: string }) => {
  const resetState = useTestClientResetStore(state => state.resetState);
  const setLastReset = useTestClientResetStore(state => state.setLastReset);
  const [isConfirming, setIsConfirming] = useState(false);
  const [isRequesting, setIsRequesting] = useState(false);

  if (!canHaveTestClientReset(login) || !resetState?.available) return null;

  const isRunning = isResetRunning(resetState.lastReset);
  const lastResetText = describeLastReset(resetState.lastReset);

  const handleConfirmReset = async () => {
    setIsConfirming(false);
    setIsRequesting(true);
    try {
      const response = await requestTestClientReset();
      if (response.lastReset) setLastReset(response.lastReset);
      if (response.alreadyRunning) {
        Alert.info(response.message);
      } else {
        Alert.success(response.message);
      }
    } catch {
      Alert.error('Não foi possível solicitar o reset. Tente de novo em instantes.');
    } finally {
      setIsRequesting(false);
    }
  };

  return (
    <>
      <Divider />
      <div>
        <button type="button" onClick={() => setIsConfirming(true)} disabled={isRequesting || isRunning}>
          <MdRestartAlt size={16} />
          <span className={styles.resetLabel}>
            <span>Resetar dados de teste</span>
            {lastResetText && <small>{lastResetText}</small>}
          </span>
        </button>
      </div>
      <ActionFeedbackDialog
        open={isConfirming}
        type="warning"
        title={`Resetar os dados do ${resetState.client}?`}
        description={CONFIRMATION_DESCRIPTION}
        primaryAction={{ label: 'Resetar', onClick: handleConfirmReset }}
        secondaryAction={{ label: 'Cancelar', onClick: () => setIsConfirming(false) }}
      />
    </>
  );
};
